import { useState, useEffect, useCallback } from 'react';

/**
 * PWA Install Hook
 *
 * Features:
 * - Detects if app is installable (beforeinstallprompt event)
 * - Tracks visit count in localStorage
 * - Provides install() function to trigger install prompt
 * - Tracks if user has dismissed the banner
 * - Detects if app is already installed
 *
 * Usage:
 * ```tsx
 * const { canInstall, isInstalled, install, shouldShowBanner, dismissBanner } = usePWA();
 *
 * if (shouldShowBanner && canInstall) {
 *   return <InstallBanner onInstall={install} onDismiss={dismissBanner} />;
 * }
 * ```
 *
 * localStorage keys:
 * - pwa-visit-count: number of visits
 * - pwa-banner-dismissed: boolean if user dismissed banner
 * - pwa-photos-uploaded: number of photos uploaded (for alternative trigger)
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEYS = {
  VISIT_COUNT: 'pwa-visit-count',
  BANNER_DISMISSED: 'pwa-banner-dismissed',
  PHOTOS_UPLOADED: 'pwa-photos-uploaded',
} as const;

const TRIGGER_THRESHOLDS = {
  VISIT_COUNT: 3,
  PHOTOS_UPLOADED: 10,
} as const;

/**
 * Detect if device is mobile/tablet (not desktop)
 * Uses multiple signals for accurate detection:
 * - Touch as primary pointer (coarse)
 * - No hover capability
 * - Screen width < 1024px
 */
function isMobileDevice(): boolean {
  // Check if touch is the primary pointer
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

  // Check if device doesn't support hover
  const noHover = window.matchMedia('(hover: none)').matches;

  // Check screen width (fallback)
  const isNarrowScreen = window.matchMedia('(max-width: 1024px)').matches;

  // Mobile if: (touch AND no-hover) OR narrow screen
  return (hasCoarsePointer && noHover) || isNarrowScreen;
}

export function usePWA() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Check if app is already installed
  useEffect(() => {
    // Check if running in standalone mode (already installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as { standalone?: boolean }).standalone === true;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsInstalled(isStandalone || isIOSStandalone);
  }, []);

  // Track visit count
  useEffect(() => {
    if (isInstalled) return;

    const visitCount = parseInt(localStorage.getItem(STORAGE_KEYS.VISIT_COUNT) || '0', 10);
    localStorage.setItem(STORAGE_KEYS.VISIT_COUNT, String(visitCount + 1));

    const dismissed = localStorage.getItem(STORAGE_KEYS.BANNER_DISMISSED) === 'true';
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBannerDismissed(dismissed);
  }, [isInstalled]);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Install function
  const install = useCallback(async () => {
    if (!deferredPrompt) {
      console.warn('Install prompt not available');
      return false;
    }

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const choiceResult = await deferredPrompt.userChoice;

    if (choiceResult.outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setDeferredPrompt(null);
      return true;
    } else {
      console.log('User dismissed the install prompt');
      return false;
    }
  }, [deferredPrompt]);

  // Dismiss banner function
  const dismissBanner = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.BANNER_DISMISSED, 'true');
    setBannerDismissed(true);
  }, []);

  // Check if banner should be shown
  const shouldShowBanner = useCallback(() => {
    // Don't show on desktop (desktop app is available)
    if (!isMobileDevice()) {
      return false;
    }

    if (isInstalled || bannerDismissed || !deferredPrompt) {
      return false;
    }

    const visitCount = parseInt(localStorage.getItem(STORAGE_KEYS.VISIT_COUNT) || '0', 10);
    const photosUploaded = parseInt(localStorage.getItem(STORAGE_KEYS.PHOTOS_UPLOADED) || '0', 10);

    // Show after 3rd visit OR after uploading 10+ photos
    return (
      visitCount >= TRIGGER_THRESHOLDS.VISIT_COUNT ||
      photosUploaded >= TRIGGER_THRESHOLDS.PHOTOS_UPLOADED
    );
  }, [isInstalled, bannerDismissed, deferredPrompt]);

  return {
    canInstall: !!deferredPrompt,
    isInstalled,
    install,
    shouldShowBanner: shouldShowBanner(),
    dismissBanner,
    isMobile: isMobileDevice(),
  };
}

/**
 * Helper hook to track photo uploads for PWA install trigger
 * Call this after successful photo upload
 */
export function useTrackPhotoUpload() {
  return useCallback(() => {
    const current = parseInt(localStorage.getItem(STORAGE_KEYS.PHOTOS_UPLOADED) || '0', 10);
    localStorage.setItem(STORAGE_KEYS.PHOTOS_UPLOADED, String(current + 1));
  }, []);
}
